import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Avatar,
  Stack,
  Container,
  IconButton,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";


import Header from "../components/Header";
import CommonBackdrop from "loader/CommonBackdrop";
import axios from "axios";
import { baseUrl, redirectUrl } from "config";
import { useSnackBar } from "context/snackbarContext";
import { useDispatch, useSelector } from "react-redux";
import { resetOrder, updateOrder } from "../slice/posSlice";
import { resetItems } from "../slice/posItemsSlice";
import { resetItems as resetPosProducts } from "../slice/posProducts";
import filterStatusValue from "utils/filterStatusValue";
import { stringToHex, hexToBase64 } from "utils/conversionUtils";
import PaymentModal from "../form/components/PaymentModal";
import ChangeBatch from "../../Sales-Sections/salesPos/components/ChangeBatch";

const GET_TABLE_DATA = "/getCurrentTableOccupiedList";
const SEND_ORDER_B2B = "/HldingStatusChangePos";
const SEND_ORDER_B2C = "/HldingStatusChangePosB2C";
const DATA_FOR_PRINT_B2B = "/getTermelPrintItems";
const DATA_FOR_PRINT_B2C = "/getTermelPrintItemsB2C";
const UPDATE_ORDER_B2B = "/updateInvoicePosMobile";
const UPDATE_ORDER_B2C = "/updateInvoicePosMobileB2C";

function Lists() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackBar();

  const commonState = useSelector((state) => state.common);
  const headerSlice = useSelector((state) => state.posSlice);
  const isB2C = headerSlice.billType === "B2C";

  const [isLoading, setIsLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [isSend, setIsSend] = useState(false);
  const [isPaymentModal, setIsPaymentModal] = useState(false);
  const [vr, setVr] = useState(0);
  const [refetch, setRefetch] = useState(false);
  const [isKotPrint, setIsKotPrint] = useState(false);
  const [isChangeBatchOpen, setIsChangeBatchOpen] = useState(false);

  const handleApiError = useCallback(
    (err, message = "Failed to fetch data") => {
      console.error("API Error:", err);
      if (err.response) {
        if (err.response.status === 403 || err.response.status === 401) {
          redirectUrl();
        } else {
          showSnackbar(
            "error",
            `${message}: ${err.response.data?.message || err.message}`
          );
        }
      } else if (err.request) {
        showSnackbar("error", `${message}: No response from server.`);
      } else {
        showSnackbar("error", `${message}: An unexpected error occurred.`);
      }
      setError(err);
    },
    [showSnackbar]
  );

  useEffect(() => {
    const getTables = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await axios.get(baseUrl + GET_TABLE_DATA, {
          params: { billType: headerSlice.billType },
          withCredentials: true,
        });

        if (res.data.success) {
          if (res?.data?.data && res.data.data.length > 0) {
            setTables(res.data.data);
          } else {
            setTables([]);
            showSnackbar("info", "No occupied tables found.");
          }
        } else {
          showSnackbar(
            "error",
            res.data.message || "Failed to retrieve tables."
          );
          setTables([]);
        }
      } catch (err) {
        handleApiError(err, "Failed to fetch tables");
      } finally {
        setIsLoading(false);
      }

    };

    getTables();
  }, [refetch]);

  useEffect(() => {
    dispatch(resetItems());
    dispatch(resetOrder());

    // Fetch current batch number for the POS section
    axios
      .get(baseUrl + "/getBatch", { withCredentials: true })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          dispatch(updateOrder({ BatchNo: res.data[0].batchno }));
        }
      })
      .catch((err) => {
        console.error("Failed to fetch batch number:", err);
      });
  }, [location.pathname, dispatch]);

  const navigateToEdit = () => {
    if (vr) {
      navigate(`/pos/form`, { state: { billNo: vr } });
    } else {
      showSnackbar("warning", "Bill No missing");
      setIsLoading(false);
    }
  };

  const handleTableClick = useCallback(
    (tableNumber, billNo = null, HldingStatus) => {
      // Reset items before switching to a new table to prevent state leakage
      dispatch(resetItems());
      dispatch(resetPosProducts());
      dispatch(resetOrder());

      dispatch(updateOrder({ tableNo: tableNumber }));
      if (HldingStatus === 2) {
        setIsSend(true);
        setVr(billNo);
      } else {
        setIsLoading(true);
        navigate(`/pos/form`);
      }
    },
    [dispatch, navigate, handleApiError, showSnackbar] // Added missing dependencies
  );

  const getCardColor = useCallback(
    (itemCount) =>
      itemCount === 2
        ? isB2C
          ? "linear-gradient(135deg, #8D6E63, #6D4C41)"
          : "linear-gradient(135deg,rgb(145, 107, 221),rgb(108, 69, 185))"
        : "linear-gradient(135deg,rgb(233, 233, 233), #bdbdbd)",
    [isB2C]
  );

  const getTextColor = useCallback(
    (itemCount) => (itemCount === 2 ? "#fff" : "#333"),
    []
  );

  // handle send Order function
  const handleSendOrder = async () => {
    setIsLoading(true);

    try {
      const SEND_ORDER = isB2C ? SEND_ORDER_B2C : SEND_ORDER_B2B;
      const res = await axios.post(
        baseUrl + SEND_ORDER,
        { BillNo: vr, HldingStatus: 1 },
        { withCredentials: true }
      );
      if (res.data.success) {
        if (commonState.isInvoice) {
          setIsKotPrint(true);
          setRefetch(!refetch);
        } else {
          setRefetch(!refetch);
        }
        setIsSend(false);
      } else {
        showSnackbar("error", "Error send Order");
      }
    } catch (err) {
      handleApiError(err, "Failed to fetch tables");
    } finally {
      setIsLoading(false);
      // setIsSend(false);
    }
  };

  const handleInitiateSendOrder = () => {
    // Set up payment data for the modal
    const currentTable = tables.find((t) => t.BillNo === vr);
    if (currentTable) {
      const billTotal = currentTable.BillNetTotalField || 0;
      dispatch(
        updateOrder({
          netTotal: billTotal,
          balance: billTotal,
          netAmount: billTotal,
          cashReceived: 0,
          bankAmount: 0,
          cashAmount: 0,
          billNo: vr,
        })
      );
    }

    if (commonState.isPaidMethod === "YES") {
      setIsSend(false);
      setIsPaymentModal(true);
    } else {
      handleSendOrder();
    }
  };

  const handlePaymentSubmit = async () => {
    setIsLoading(true);
    try {
      // Update payment details first
      const UPDATE_ORDER = isB2C ? UPDATE_ORDER_B2C : UPDATE_ORDER_B2B;
      const res = await axios.post(
        baseUrl + UPDATE_ORDER,
        {
          header: {
            ...headerSlice,
            billNo: vr,
            date: dayjs(),
            deliveryDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          },
          tableData: [],
          paymentOnly: true,
        },
        { withCredentials: true }
      );

      if (res.data.success) {
        // Payment saved, now send order
        setIsPaymentModal(false);
        handleSendOrder();
      } else {
        showSnackbar("error", res.data.message);
        setIsLoading(false);
      }
    } catch (error) {
      handleApiError(error, "Failed to update payment");
      setIsLoading(false);
    }
  };

  // to get master data and table data for print
  const handlePrintInvoice = () => {
    const DATA_FOR_PRINT = isB2C ? DATA_FOR_PRINT_B2C : DATA_FOR_PRINT_B2B;
    axios
      .post(
        baseUrl + DATA_FOR_PRINT,
        {
          BillNo: vr,
          typ: "SB",
          invoicePrint: true,
        },
        { withCredentials: true }
      )
      .then((response) => {
        const allData = response.data;
        const billData = allData?.[0]?.[0];

        // Generate QR Data
        let qrData = "";
        const companyNameOg = commonState?.companyInfo?.Name || "";
        const vatNumberOg = commonState?.companyInfo?.Add2 || "";

        // We need at least Company Name and VAT Number to generate a valid ZATCA QR
        if (companyNameOg && vatNumberOg) {
          try {
            const billTotal = billData?.BillNetTotalField || 0;
            const billTax = billData?.TItTaxAmt || 0;

            // Use bill date or fallback to current time
            let inputDate;
            if (billData?.BillDate) {
              inputDate = new Date(billData.BillDate);
              inputDate.setMinutes(inputDate.getMinutes() - inputDate.getTimezoneOffset());
            } else {
              inputDate = new Date();
            }

            const invoiceDateOg = inputDate.toISOString();

            let companyNameOgHex = stringToHex(companyNameOg);
            let companyNameOgLeng =
              companyNameOg.length.toString(16).length === 1
                ? `0${companyNameOg.length.toString(16)}`
                : companyNameOg.length.toString(16);

            let vatNumberOgHex = stringToHex(vatNumberOg);
            let vatNumberOgLeng =
              vatNumberOg.length.toString(16).length === 1
                ? `0${vatNumberOg.length.toString(16)}`
                : vatNumberOg.length.toString(16);

            let invoiceDateHex = stringToHex(invoiceDateOg);
            let invoiceDateOGLeng =
              invoiceDateOg.length.toString(16).length === 1
                ? `0${invoiceDateOg.length.toString(16)}`
                : invoiceDateOg.length.toString(16);

            let totalOg = (billTotal || 0).toString();
            let totalHex = stringToHex(totalOg);
            let totalOgLeng =
              totalOg.length.toString(16).length === 1
                ? `0${totalOg.length.toString(16)}`
                : totalOg.length.toString(16);

            let vatTotalOg = (billTax || 0).toFixed(3).toString();
            let vatTotalHex = stringToHex(vatTotalOg);
            let vatTotalOgLeng =
              vatTotalOg.length.toString(16).length === 1
                ? `0${vatTotalOg.length.toString(16)}`
                : vatTotalOg.length.toString(16);

            let value = `01${companyNameOgLeng}${companyNameOgHex}02${vatNumberOgLeng}${vatNumberOgHex}03${invoiceDateOGLeng}${invoiceDateHex}04${totalOgLeng}${totalHex}05${vatTotalOgLeng}${vatTotalHex}`;
            qrData = hexToBase64(value);
          } catch (error) {
            qrData = "";
          }
        }

        // Priority: WEB_PRINT → PosPrint → B2BPrint
        let selectedPrinterType;

        // Try WEB_PRINT first (Thermal printer)
        const webPrint = filterStatusValue(commonState?.printrtTyp?.[0], "Typ", "WEB_PRINT");

        if (webPrint && webPrint.length > 0 && webPrint[0]?.Description) {
          selectedPrinterType = webPrint[0].Description;
        } else {
          // Try PosPrint as second option
          const posPrint = filterStatusValue(commonState?.printrtTyp?.[0], "Typ", "PosPrint");

          if (posPrint && posPrint.length > 0 && posPrint[0]?.Description) {
            selectedPrinterType = posPrint[0].Description;
          } else {
            // Fallback to B2BPrint
            const b2bPrint = filterStatusValue(commonState?.printrtTyp?.[0], "Typ", "B2BPrint");
            selectedPrinterType = b2bPrint[0]?.Description;
          }
        }

        navigate("/printer/printerCheck", {
          state: {
            data: allData[0][0],
            tableData: allData[1],
            qrData: qrData,
            printType: selectedPrinterType,
            typ: "SB",

            isInvoiceData: {
              invoiceOnly: commonState.isInvoice,
              isInvoice: commonState.isInvoice,
              isKot: false,
              isALLKot: false,
            },

            kotTableData: [],
            kotNo: allData[0][0]?.OrderNo, // KOT number from API response
            navigateBackThermalDirect: -1,
            navigateBack: -1,
          },
        });
        setRefetch(!refetch);
        // navigate(-2);
      })

      .catch((error) => {
        if (
          (error.response && error.response.status === 403) ||
          error.response.status === 401
        ) {
          redirectUrl();
        }
      });
  };

  if (error && !isLoading) {
    return (
      <Container>
        <Header />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 64px)"
          textAlign="center"
          p={4}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Oops! Couldn't load tables.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please check your network connection or try again later.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <CommonBackdrop open={isLoading} />
      <Header />

      <Dialog
        open={isSend}
        onClose={() => {
          setIsSend(false);
          setIsLoading(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Confirm your action
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsSend(false);
              setIsLoading(false);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
            Please choose what you want to do with this order.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={isLoading}
            onClick={() => handleInitiateSendOrder()}
            color="primary"
            variant="contained"
          >
            Send Order
          </Button>
          <Button
            disabled={isLoading}
            onClick={() => navigateToEdit()}
            color="secondary"
            variant="contained"
          >
            Edit Order
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isKotPrint}
        onClose={() => {
          setIsKotPrint(false);
          setIsLoading(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Print
          <IconButton
            disabled={isLoading}
            aria-label="close"
            onClick={() => {
              setIsKotPrint(false);
              setIsLoading(false);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
            Do you want to print invoice?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={isLoading}
            color="primary"
            variant="contained"
            onClick={() => {
              setIsKotPrint(false);
              setIsLoading(false);
              setRefetch(!refetch);
            }}
          >
            No
          </Button>
          <Button
            disabled={isLoading}
            color="secondary"
            variant="contained"
            onClick={handlePrintInvoice}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isPaymentModal}
        onClose={() => setIsPaymentModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm payment</DialogTitle>
        <DialogContent>
          <PaymentModal
            onClose={() => setIsPaymentModal(false)}
            onSave={handlePaymentSubmit}
            loading={isLoading}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isChangeBatchOpen}
        onClose={() => setIsChangeBatchOpen(false)}
        aria-labelledby="change-batch-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <ChangeBatch handleDialogClose={setIsChangeBatchOpen} />
        </DialogContent>
      </Dialog>

      <Container>
        <Box sx={{ padding: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              Tables
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={() => setIsChangeBatchOpen(true)}
                sx={{
                  borderRadius: '30px',
                  px: 4,
                  py: 1.5,
                  background: isB2C ? '#8D6E63' : 'rgb(108, 69, 185)',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  '&:hover': {
                    background: isB2C ? '#6D4C41' : 'rgb(88, 49, 165)',
                    boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  transition: 'background 0.3s ease, box-shadow 0.3s ease',
                }}
              >
                Change Batch
              </Button>
              <Button
                variant="contained"
                startIcon={
                  <RefreshIcon
                    sx={{
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                      animation: 'spin 3s linear infinite',
                    }}
                  />
                }
                onClick={() => setRefetch(!refetch)}
                sx={{
                  '@keyframes gentleFloat': {
                    '0%': { transform: 'translate(0, 0)' },
                    '25%': { transform: 'translate(2px, -3px)' },
                    '50%': { transform: 'translate(-1px, 2px)' },
                    '75%': { transform: 'translate(-2px, -1px)' },
                    '100%': { transform: 'translate(0, 0)' },
                  },
                  '@keyframes glowPulse': {
                    '0%, 100%': {
                      boxShadow: isB2C ? '0 4px 15px rgba(109, 76, 65, 0.3)' : '0 4px 15px rgba(108, 69, 185, 0.3)',
                    },
                    '50%': {
                      boxShadow: isB2C ? '0 8px 30px rgba(141, 110, 99, 0.6), 0 0 20px rgba(141, 110, 99, 0.3)' : '0 8px 30px rgba(145, 107, 221, 0.6), 0 0 20px rgba(145, 107, 221, 0.3)',
                    },
                  },
                  borderRadius: '30px',
                  px: 5,
                  py: 1.5,
                  background: isB2C ? 'linear-gradient(135deg, #6D4C41, #8D6E63)' : 'linear-gradient(135deg, rgb(108, 69, 185), rgb(145, 107, 221))',
                  animation: 'gentleFloat 4s ease-in-out infinite, glowPulse 2.5s ease-in-out infinite',
                  '&:hover': {
                    animation: 'none',
                    background: isB2C ? 'linear-gradient(135deg, #5D4037, #795548)' : 'linear-gradient(135deg, rgb(125, 85, 200), rgb(160, 120, 240))',
                    boxShadow: isB2C ? '0 12px 35px rgba(109, 76, 65, 0.5)' : '0 12px 35px rgba(108, 69, 185, 0.5)',
                    transform: 'translateY(-3px) scale(1.03)',
                  },
                  '&:active': {
                    transform: 'translateY(0px) scale(0.97)',
                  },
                  transition: 'transform 0.2s ease, background 0.3s ease',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                }}
              >
                Reload Tables
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={2}>
            {tables.length > 0
              ? tables.map((table) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={table.Typ}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      background: getCardColor(table.HldingStatus),
                      color: getTextColor(table.HldingStatus),
                      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() =>
                        handleTableClick(
                          table.Typ,
                          table.BillNo,
                          table.HldingStatus
                        )
                      }
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems="center"
                          >
                            <Avatar
                              sx={{
                                bgcolor:
                                  table.HldingStatus === 2
                                    ? "#fff"
                                    : "#424242",
                                color:
                                  table.HldingStatus === 2
                                    ? isB2C ? "#6D4C41" : "rgb(108, 69, 185)"
                                    : "#fff",
                                width: 44,
                                height: 44,
                              }}
                            >
                              <TableRestaurantIcon fontSize="small" />
                            </Avatar>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: getTextColor(table.HldingStatus),
                              }}
                              fontWeight={600}
                            >
                              Table {table.Typ}
                            </Typography>
                          </Stack>

                          <Stack
                            direction="column"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="flex-end"
                            sx={{ mt: { xs: 2, sm: 0 } }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <ShoppingCartIcon
                                fontSize="small"
                                sx={{
                                  color: getTextColor(table.HldingStatus),
                                }}
                              />
                              <Typography
                                sx={{
                                  color: getTextColor(table.HldingStatus),
                                }}
                                variant="body2"
                              >
                                {table.HldingStatus === 2
                                  ? table.SalesBillCount
                                  : 0}
                                &nbsp;items
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography
                                sx={{
                                  color: getTextColor(table.HldingStatus),
                                }}
                                variant="body1"
                              >
                                {table.HldingStatus === 2
                                  ? table.BillNetTotalField.toFixed(
                                    commonState.decimalCount || 0
                                  )
                                  : 0}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))
              : !isLoading && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height="calc(100vh - 200px)"
                    textAlign="center"
                    p={4}
                  >
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      No tables currently occupied.
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      New orders can be started from the form.
                    </Typography>
                  </Box>
                </Grid>
              )}
          </Grid>

        </Box>
      </Container>

    </>
  );
}

export default Lists;